// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from "react";
import Wizard from "@cloudscape-design/components/wizard";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Flashbar, { type FlashbarProps } from "@cloudscape-design/components/flashbar";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import type { ILocalAgentClient } from "../services/localAgentClient";
import type { IBackendClient } from "../services/backendClient";
import type { IWebCsrValidator } from "./csrValidator";
import ConnectYubiKeyStep from "./steps/ConnectYubiKeyStep";
import GenerateKeyStep from "./steps/GenerateKeyStep";
import IssueCertificateStep from "./steps/IssueCertificateStep";
import StoreCertificateStep from "./steps/StoreCertificateStep";
import EnrollmentSummary from "./EnrollmentSummary";

export type StepStatus = "pending" | "in-progress" | "completed" | "failed";

export interface WizardState {
  activeStepIndex: number;
  stepStatuses: StepStatus[];
  error: string | null;
  yubiKeySerial: string | null;
  csrPem: string | null;
  certificatePem: string | null;
  chainPem: string | null;
  certificateSubject: string | null;
  certificateSerial: string | null;
  certificateNotAfter: string | null;
}

export interface WizardStepProps {
  state: WizardState;
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  onComplete: () => void;
  onError: (message: string) => void;
}

export interface EnrollmentWizardProps {
  localAgentClient?: ILocalAgentClient;
  backendClient?: IBackendClient;
  csrValidator?: IWebCsrValidator;
}

const STEP_LABELS = [
  "Connect YubiKey",
  "Generate Key",
  "Issue Certificate",
  "Store Certificate",
];

const INITIAL_STATE: WizardState = {
  activeStepIndex: 0,
  stepStatuses: ["pending", "pending", "pending", "pending"],
  error: null,
  yubiKeySerial: null,
  csrPem: null,
  certificatePem: null,
  chainPem: null,
  certificateSubject: null,
  certificateSerial: null,
  certificateNotAfter: null,
};

export function statusIndicatorType(
  status: StepStatus
): "pending" | "in-progress" | "success" | "error" {
  switch (status) {
    case "pending":
      return "pending";
    case "in-progress":
      return "in-progress";
    case "completed":
      return "success";
    case "failed":
      return "error";
  }
}

export function statusLabel(status: StepStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "in-progress":
      return "In progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
  }
}

export default function EnrollmentWizard(
  props: EnrollmentWizardProps
): React.ReactElement {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);

  const handleStepComplete = () => {
    setState((prev) => {
      const newStatuses = [...prev.stepStatuses];
      newStatuses[prev.activeStepIndex] = "completed";
      const nextIndex = prev.activeStepIndex + 1;
      if (nextIndex < STEP_LABELS.length) {
        newStatuses[nextIndex] = "pending";
      }
      return {
        ...prev,
        stepStatuses: newStatuses,
        activeStepIndex: nextIndex,
        error: null,
      };
    });
  };

  const handleStepError = (message: string) => {
    setState((prev) => {
      const newStatuses = [...prev.stepStatuses];
      newStatuses[prev.activeStepIndex] = "failed";
      return {
        ...prev,
        stepStatuses: newStatuses,
        error: message,
      };
    });
  };

  const handleRetry = () => {
    setState((prev) => {
      const newStatuses = [...prev.stepStatuses];
      newStatuses[prev.activeStepIndex] = "pending";
      return {
        ...prev,
        stepStatuses: newStatuses,
        error: null,
      };
    });
  };

  const errorItems: FlashbarProps.MessageDefinition[] = state.error
    ? [
        {
          type: "error",
          content: state.error,
          dismissible: false,
          action: (
            <button type="button" onClick={handleRetry}>
              Retry
            </button>
          ),
          id: "step-error",
        },
      ]
    : [];

  const isFinished = state.activeStepIndex >= STEP_LABELS.length;

  if (isFinished) {
    return (
      <EnrollmentSummary
        certificateSubject={state.certificateSubject}
        certificateSerial={state.certificateSerial}
        certificateNotAfter={state.certificateNotAfter}
      />
    );
  }

  const stepContent = (label: string, index: number): React.ReactNode => {
    if (index === 0 && props.localAgentClient) {
      return (
        <SpaceBetween size="m">
          {state.error && index === state.activeStepIndex && (
            <Flashbar items={errorItems} />
          )}
          <ConnectYubiKeyStep
            state={state}
            setState={setState}
            onComplete={handleStepComplete}
            onError={handleStepError}
            localAgentClient={props.localAgentClient}
          />
        </SpaceBetween>
      );
    }
    if (index === 1 && props.localAgentClient) {
      return (
        <SpaceBetween size="m">
          {state.error && index === state.activeStepIndex && (
            <Flashbar items={errorItems} />
          )}
          <GenerateKeyStep
            state={state}
            setState={setState}
            onComplete={handleStepComplete}
            onError={handleStepError}
            localAgentClient={props.localAgentClient}
            csrValidator={props.csrValidator}
          />
        </SpaceBetween>
      );
    }
    if (index === 2 && props.backendClient) {
      return (
        <SpaceBetween size="m">
          {state.error && index === state.activeStepIndex && (
            <Flashbar items={errorItems} />
          )}
          <IssueCertificateStep
            state={state}
            setState={setState}
            onComplete={handleStepComplete}
            onError={handleStepError}
            backendClient={props.backendClient}
          />
        </SpaceBetween>
      );
    }
    if (index === 3 && props.localAgentClient) {
      return (
        <SpaceBetween size="m">
          {state.error && index === state.activeStepIndex && (
            <Flashbar items={errorItems} />
          )}
          <StoreCertificateStep
            state={state}
            setState={setState}
            onComplete={handleStepComplete}
            onError={handleStepError}
            localAgentClient={props.localAgentClient}
          />
        </SpaceBetween>
      );
    }
    return (
      <SpaceBetween size="m">
        {state.error && index === state.activeStepIndex && (
          <Flashbar items={errorItems} />
        )}
        <Container>
          <p>
            {label} — {statusLabel(state.stepStatuses[index])}
          </p>
        </Container>
      </SpaceBetween>
    );
  };

  const steps = STEP_LABELS.map((label, index) => ({
    title: label,
    description: (
      <StatusIndicator type={statusIndicatorType(state.stepStatuses[index])}>
        {statusLabel(state.stepStatuses[index])}
      </StatusIndicator>
    ),
    content: stepContent(label, index),
  }));

  return (
    <Wizard
      i18nStrings={{
        stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
        collapsedStepsLabel: (stepNumber, stepsCount) =>
          `Step ${stepNumber} of ${stepsCount}`,
        submitButton: "Complete enrollment",
        previousButton: "Previous",
        nextButton: "Next",
        cancelButton: "Cancel",
        optional: "optional",
      }}
      steps={steps}
      activeStepIndex={state.activeStepIndex}
      onNavigate={({ detail }) => {
        // Only allow navigation if current step is completed
        if (
          detail.requestedStepIndex > state.activeStepIndex &&
          state.stepStatuses[state.activeStepIndex] !== "completed"
        ) {
          return;
        }
        setState((prev) => ({
          ...prev,
          activeStepIndex: detail.requestedStepIndex,
        }));
      }}
    />
  );
}


